import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { homesAdvisor } from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";
import { fmtNum, fmtPrice } from "@/components/homes/homesUtil";

const EXAMPLES = [
  "I have $600k and want a warm, walkable city with great food and an airport nearby",
  "Quiet suburb, good for remote work, under $450k, mild winters",
  "Big-city energy with condos under $500k and lots of nightlife",
];

export default function AISearch() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setQ(query); setLoading(true); setErr(""); setRes(null);
    try {
      const data = await homesAdvisor(query);
      if (data.unavailable) setErr(data.message);
      else setRes(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "The advisor is unavailable right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="homes-advisor">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1100px] px-4 py-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-sm font-semibold text-[#93B4FF]">
            <Sparkles className="h-4 w-4" /> AI Relocation Advisor
          </span>
          <h1 className="mt-5 text-4xl font-black leading-tight md:text-6xl">Where should I live?</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">
            Describe your budget and lifestyle. Our advisor recommends the best places first — then shows you homes there. It reasons over the USAPulse geographic graph.
          </p>

          <div className="mt-8 rounded-2xl bg-white p-2 shadow-2xl">
            <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3}
              placeholder="e.g. I want a sunny city under $600k with great restaurants and an easy commute"
              className="w-full resize-none rounded-xl bg-transparent p-4 text-[#0F172A] outline-none" data-testid="advisor-input" />
            <div className="flex justify-end p-1">
              <button onClick={() => run()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl bg-[#2563EB] px-7 py-3 font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="advisor-submit">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Thinking…</> : <><Sparkles className="h-5 w-5" /> Get recommendations</>}
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => run(ex)} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70 transition-colors hover:border-white/40 hover:text-white" data-testid="advisor-example">
                {ex}
              </button>
            ))}
          </div>
          <p className="mt-4 flex items-center gap-1.5 text-xs text-white/40"><ShieldCheck className="h-3.5 w-3.5" /> Fair-Housing compliant — recommendations use only objective factors (budget, climate, amenities, lifestyle), never protected characteristics.</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-4 py-10">
        {err && <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="advisor-error">{err}</div>}
        {loading && <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>}

        {res && (
          <div data-testid="advisor-results">
            {res.summary && <p className="mb-6 text-lg font-semibold text-[#0F172A]">{res.summary}</p>}
            <div className="space-y-10">
              {res.recommendations.map((r, i) => (
                <div key={r.geo_id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`advisor-rec-${r.geo_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2563EB] text-sm font-black text-white">{i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <Link to={`/homes/place/${r.geo_id}`} className="text-2xl font-black text-[#0F172A] hover:text-[#2563EB]">{r.name}, {r.state}</Link>
                      <p className="mt-1 text-slate-600">{r.reason}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
                        {r.population != null && <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-[#2563EB]" /> {fmtNum(r.population)} people</span>}
                        {r.median_sale_price && <span>Median {fmtPrice(r.median_sale_price)}</span>}
                      </div>
                    </div>
                    <Link to={`/homes/place/${r.geo_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF2FF] px-4 py-2 text-sm font-semibold text-[#2563EB] hover:bg-[#2563EB] hover:text-white">
                      Explore <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                  {r.listings?.length > 0 && (
                    <div className="mt-5 grid gap-4 sm:grid-cols-3">
                      {r.listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
